import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Section from '../components/Section';
import WeekTabs from '../components/WeekTabs';
import HeaderBar from '../components/HeaderBar';
import type { DiaryItem } from '../models/diary';
import DiaryItemCard from '../components/DiaryItemCard';
import AdherenceChart from '../components/AdherenceChart';
import { adherenceService, AdherenceStatistic } from '../services/adherenceService';
import Tabs from '../components/Tabs';
import EditPatientModal from '../components/EditPatientModal';
import DiaryItemCreateModal from '../components/DiaryItemCreateModal';
import DocumentManager from '../components/DocumentManager';
import { patientsService, Patient, UpdatePatientData } from '../services/patientsService';
import { diaryService } from '../services/diaryService';
import { useAuth } from '../hooks/useAuth';





const PatientDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [selectedDay, setSelectedDay] = useState(1); // Default to Martes
  const [editPatientModalOpen, setEditPatientModalOpen] = useState(false);
  const [patient, setPatient] = useState<Patient | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState(0); // 0: Statistics, 1: Plan, 2: Other
  const [planItems, setPlanItems] = useState<DiaryItem[]>([]);
  const [diaryLoading, setDiaryLoading] = useState(false);
  const [diaryError, setDiaryError] = useState<string | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<DiaryItem | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createModalItemType, setCreateModalItemType] = useState<'FitnessItem' | 'SupplementItem'>('FitnessItem');
  const [adherenceData, setAdherenceData] = useState<AdherenceStatistic[]>([]);
  const [adherenceLoading, setAdherenceLoading] = useState(false);
  const [adherenceError, setAdherenceError] = useState<string | null>(null);
  const [draggedItem, setDraggedItem] = useState<DiaryItem | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  
  // Map selectedDay to day names for filtering diary items
  const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday','Sunday'];
  const selectedDayName = dayNames[selectedDay];
  
  // Filter diary items by selected day and sort by order
  const filteredPlanItems = planItems
    .filter(item => 
      item.dayOfWeek === selectedDayName || 
      (item.dayOfWeek && item.dayOfWeek.toLowerCase() === selectedDayName.toLowerCase())
    )
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  // Fetch patient data when component mounts or ID changes
  useEffect(() => {
    const fetchPatient = async () => {
      if (!id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const patientData = await patientsService.getPatientById(id);
        setPatient(patientData);
      } catch (err) {
        console.error('Error fetching patient:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPatient();
  }, [id]);

  // Fetch diary items when patient ID changes
  useEffect(() => {
    const fetchDiaryItems = async () => {
      if (!id) return;

      try {
        setDiaryLoading(true);
        setDiaryError(null);
        const diaryItems = await diaryService.getAllDiaryItemsByUserId(id);
        // Sort items by order field when initially loading
        const sortedItems = diaryItems.sort((a, b) => (a.order || 0) - (b.order || 0));
        setPlanItems(sortedItems);
      } catch (err) {
        setDiaryError('Failed to load diary items');
        console.error('Error fetching diary items:', err);
        setPlanItems([]);
      } finally {
        setDiaryLoading(false);
      }
    };

    fetchDiaryItems();
  }, [id]);

  // Fetch adherence data when patient data is loaded
  useEffect(() => {
    const fetchAdherenceData = async () => {
      if (!patient?.userId) return;

      try {
        setAdherenceLoading(true);
        setAdherenceError(null);
        const adherenceStats = await adherenceService.getLastTenWeeksAdherence(patient.userId);
        setAdherenceData(adherenceStats);
      } catch (err) {
        setAdherenceError('Failed to load adherence data');
        console.error('Error fetching adherence data:', err);
        setAdherenceData([]);
      } finally {
        setAdherenceLoading(false);
      }
    };

    fetchAdherenceData();
  }, [patient?.userId]);


  // Open modal for editing
  const handleEditItem = (item: DiaryItem) => {
    setEditingItem({ ...item });
    setEditModalOpen(true);
  };
  
  // Save changes (receives updated data from the unified modal)
  const handleSaveEdit = async (updatedItemData: Partial<DiaryItem>) => {
    if (!editingItem || !id) return;
    
    try {
      // Merge the updated data with the existing item to create a complete DiaryItem
      const completeItem: DiaryItem = {
        ...editingItem,
        ...updatedItemData,
        id: editingItem.id // Ensure ID is preserved
      };
      
      // Call the API to update the diary item with the merged data
      await diaryService.updateDiaryItem(id, editingItem.id, completeItem);
      
      // Refetch all diary items to get the latest data from the server
      const diaryItems = await diaryService.getAllDiaryItemsByUserId(id);
      const sortedItems = diaryItems.sort((a, b) => (a.order || 0) - (b.order || 0));
      setPlanItems(sortedItems);
      
      setEditModalOpen(false);
      setEditingItem(null);
    } catch (error) {
      console.error('Error updating diary item:', error);
      alert('Error al guardar los cambios. Por favor, inténtalo de nuevo.');
    }
  };
  // Cancel edit
  const handleCancelEdit = () => {
    setEditModalOpen(false);
    setEditingItem(null);
  };

  // Handle create plan
  const handleCreatePlan = async (newItem: Partial<DiaryItem>) => {
    if (!id) return;
    
    try {
      // Call the API to create the diary item
      await diaryService.createDiaryItem(id, newItem);
      
      // Refetch all diary items to get the latest data from the server
      const diaryItems = await diaryService.getAllDiaryItemsByUserId(id);
      const sortedItems = diaryItems.sort((a, b) => (a.order || 0) - (b.order || 0));
      setPlanItems(sortedItems);
      
      setCreateModalOpen(false);
    } catch (error) {
      console.error('Error creating diary item:', error);
      alert('Error al crear el plan. Por favor, inténtalo de nuevo.');
    }
  };

  // Cancel create
  const handleCancelCreate = () => {
    setCreateModalOpen(false);
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, item: DiaryItem) => {
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = async (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    setDragOverIndex(null);

    if (!draggedItem || !id) return;

    const dragIndex = filteredPlanItems.findIndex(item => item.id === draggedItem.id);
    if (dragIndex === dropIndex) return;

    // Create new array with reordered items
    const newItems = [...filteredPlanItems];
    const [removed] = newItems.splice(dragIndex, 1);
    newItems.splice(dropIndex, 0, removed);

    // Update order values based on new positions
    const updatedItems = newItems.map((item, index) => ({
      ...item,
      order: index + 1
    }));

    // Optimistically update the UI
    setPlanItems(prevItems => {
      const otherDayItems = prevItems.filter(item => 
        item.dayOfWeek !== selectedDayName && 
        item.dayOfWeek?.toLowerCase() !== selectedDayName.toLowerCase()
      );
      return [...otherDayItems, ...updatedItems].sort((a, b) => (a.order || 0) - (b.order || 0));
    });

    // Update all affected items in the backend
    try {
      const updatePromises = updatedItems.map(item => 
        diaryService.updateDiaryItem(id, item.id, item)
      );
      await Promise.all(updatePromises);
    } catch (error) {
      console.error('Error updating item order:', error);
      // Revert the optimistic update on error
      const revertedItems = await diaryService.getAllDiaryItemsByUserId(id);
      setPlanItems(revertedItems.sort((a, b) => (a.order || 0) - (b.order || 0)));
      alert('Error al reordenar los elementos. Por favor, inténtalo de nuevo.');
    }

    setDraggedItem(null);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverIndex(null);
  };

  // Handle patient update
  const handleUpdatePatient = async (data: UpdatePatientData) => {
    if (!id) return;
    
    try {
      await patientsService.updatePatient(id, data);
      setEditPatientModalOpen(false);
      // Refetch patient data from API
      const patientData = await patientsService.getPatientById(id);
      setPatient(patientData);
    } catch (error) {
      console.error('Failed to update patient:', error);
      throw error; // Re-throw to let the modal handle the error display
    }
  };

  if (loading) {
    return (
      <div style={{ background: '#f9fafb', minHeight: '100vh', padding: '0' }}>
        <HeaderBar title="Detalle de paciente" onBack={() => navigate('/app')} rightContent={<button onClick={handleLogout} style={{background:'none',border:'none',color:'#38b2ac',fontWeight:600,fontSize:16,cursor:'pointer'}}>Logout</button>} />
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '40px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: 18, color: '#666' }}>Cargando datos del paciente...</div>
        </div>
      </div>
    );
  }


  if (!patient) {
    return (
      <div style={{ background: '#f9fafb', minHeight: '100vh', padding: '0' }}>
        <HeaderBar title="Detalle de paciente" onBack={() => navigate('/app')} rightContent={<button onClick={handleLogout} style={{background:'none',border:'none',color:'#38b2ac',fontWeight:600,fontSize:16,cursor:'pointer'}}>Logout</button>} />
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '40px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: 18, color: '#666' }}>Paciente no encontrado.</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: '#f9fafb', minHeight: '100vh', padding: '0' }}>
      <HeaderBar title="Detalle de paciente" onBack={() => navigate('/app')} rightContent={<button onClick={handleLogout} style={{background:'none',border:'none',color:'#38b2ac',fontWeight:600,fontSize:16,cursor:'pointer'}}>Logout</button>} />
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 40, marginBottom: 32 }}>
          {/* Left: Patient Info */}
          <Section style={{ minWidth: 320, maxWidth: 360, flex: '0 0 340px', marginBottom: 0 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
              {/* Avatar */}
              <div style={{ width: 100, height: 100, borderRadius: '50%', overflow: 'hidden', position: 'relative' }}>
                {patient.avatarUrl ? (
                  <img 
                    src={patient.avatarUrl} 
                    alt={`${patient.firstName} ${patient.lastName}`}
                    style={{ 
                      width: '100%', 
                      height: '100%', 
                      objectFit: 'cover',
                      display: 'block'
                    }}
                  />
                ) : (
                  <div style={{ 
                    width: '100%', 
                    height: '100%', 
                    background: '#38b2ac', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    fontSize: 40, 
                    fontWeight: 700, 
                    color: '#fff' 
                  }}>
                    {(patient.firstName || '').charAt(0).toUpperCase()}{(patient.lastName || '').charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              <div style={{ fontWeight: 700, fontSize: 22, color: '#222', textAlign: 'center' }}>
                {patient.firstName || ''} {patient.lastName || ''}
              </div>
              <div style={{ color: '#6b7280', fontSize: 14, textAlign: 'center' }}>
                @{patient.username || ''}
              </div>
              <button 
                onClick={() => setEditPatientModalOpen(true)} 
                style={{
                  marginTop: 8,
                  background: '#38b2ac',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  padding: '8px 24px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: 14
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#319795';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#38b2ac';
                }}
              >
                Editar Datos
              </button>
            </div>
            
            {/* Patient Info Display */}
            <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase' }}>Email</div>
                <div style={{ fontSize: 15, color: '#1f2937' }}>{patient.email || '-'}</div>
              </div>
              
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase' }}>Teléfono</div>
                <div style={{ fontSize: 15, color: '#1f2937' }}>{patient.phone_number || '-'}</div>
              </div>
              
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase' }}>Género</div>
                <div style={{ fontSize: 15, color: '#1f2937' }}>
                  {patient.gender === 'male' ? 'Masculino' : patient.gender === 'female' ? 'Femenino' : patient.gender === 'other' ? 'Otro' : '-'}
                </div>
              </div>
              
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase' }}>Dirección</div>
                <div style={{ fontSize: 15, color: '#1f2937' }}>{patient.address || '-'}</div>
              </div>
              
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase' }}>Fecha de Nacimiento</div>
                <div style={{ fontSize: 15, color: '#1f2937' }}>
                  {patient.date_of_birth ? new Date(patient.date_of_birth).toLocaleDateString('es-ES') : '-'}
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: 16 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase' }}>RMR</div>
                  <div style={{ fontSize: 15, color: '#1f2937' }}>{patient.rmrKcalDay || '-'} {patient.rmrKcalDay ? 'kcal/día' : ''}</div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase' }}>Peso</div>
                  <div style={{ fontSize: 15, color: '#1f2937' }}>{patient.weight || '-'} {patient.weight ? 'kg' : ''}</div>
                </div>
              </div>
            </div>
          </Section>
          {/* Right: Plan, Tabs, Graphs */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <Tabs tabs={["Estadísticas", "Plan", "Documentos"]} selected={selectedTab} onSelect={setSelectedTab} />
            {selectedTab === 0 && (
              <>
                <Section>
                  <div style={{ fontWeight: 700, marginBottom: 8, color: '#222' }}>Grafica Adherencia al plan</div>
                  <AdherenceChart 
                    data={adherenceData}
                    loading={adherenceLoading}
                    error={adherenceError}
                  />
                </Section>
              </>
            )}
            {selectedTab === 1 && (
              <Section style={{ flex: 1, marginBottom: 0 }}>
                <div style={{ display: 'flex', flexDirection: 'column', marginBottom: 16, gap: 12 }}>
                  <WeekTabs selected={selectedDay} onSelect={setSelectedDay} />
                  <div style={{ display: 'flex', gap: 12 }}>
                    <button
                      onClick={() => {
                        setCreateModalItemType('FitnessItem');
                        setCreateModalOpen(true);
                      }}
                      style={{
                        background: '#38b2ac',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 6,
                        padding: '8px 16px',
                        fontWeight: 600,
                        fontSize: 14,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#319795';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#38b2ac';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      <span style={{ fontSize: 18, lineHeight: 1 }}>+</span>
                      <span style={{ fontSize: 16 }}>🏃</span>
                      <span>Actividad</span>
                    </button>
                    <button
                      onClick={() => {
                        setCreateModalItemType('SupplementItem');
                        setCreateModalOpen(true);
                      }}
                      style={{
                        background: '#8b5cf6',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 6,
                        padding: '8px 16px',
                        fontWeight: 600,
                        fontSize: 14,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#7c3aed';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#8b5cf6';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      <span style={{ fontSize: 18, lineHeight: 1 }}>+</span>
                      <span style={{ fontSize: 16 }}>💊</span>
                      <span>Suplemento</span>
                    </button>
                  </div>
                </div>
                {diaryLoading ? (
                  <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>
                    Cargando plan...
                  </div>
                ) : diaryError ? (
                  <div style={{ padding: 40, textAlign: 'center' }}>
                    <div style={{ color: '#e53e3e', marginBottom: 16 }}>{diaryError}</div>
                  </div>
                ) : filteredPlanItems.length === 0 ? (
                  <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>
                    No hay actividades planificadas para {selectedDayName}
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {filteredPlanItems.map((item, index) => (
                      <div 
                        key={`${item.id}-${item.dayOfWeek}-${index}`} 
                        style={{ 
                          position: 'relative',
                          opacity: draggedItem?.id === item.id ? 0.5 : 1,
                          transform: dragOverIndex === index ? 'translateY(-2px)' : 'none',
                          transition: 'all 0.2s ease',
                          borderTop: dragOverIndex === index ? '3px solid #38b2ac' : 'none',
                          paddingTop: dragOverIndex === index ? '8px' : '0'
                        }}
                        draggable
                        onDragStart={(e) => handleDragStart(e, item)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, index)}
                        onDragEnd={handleDragEnd}
                      >
                        <div style={{ 
                          cursor: 'grab',
                          position: 'relative'
                        }}>
                          <DiaryItemCard item={item} />
                          {/* Drag handle indicator */}
                          <div style={{
                            position: 'absolute',
                            left: 8,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: '#999',
                            fontSize: 16,
                            cursor: 'grab',
                            userSelect: 'none'
                          }}>
                            ⋮⋮
                          </div>
                          <button
                            onClick={() => handleEditItem(item)}
                            style={{ position: 'absolute', top: 10, right: 10, background: '#38b2ac', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 12px', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}
                          >Editar</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <DiaryItemCreateModal
                  open={editModalOpen}
                  onSave={handleSaveEdit}
                  onCancel={handleCancelEdit}
                  editingItem={editingItem}
                  mode="edit"
                  patientUserId={patient?.userId}
                />
                <DiaryItemCreateModal
                  open={createModalOpen}
                  onSave={handleCreatePlan}
                  onCancel={handleCancelCreate}
                  defaultDayOfWeek={selectedDayName}
                  defaultItemType={createModalItemType}
                  patientUserId={patient?.userId}
                />
              </Section>
            )}
            {selectedTab === 2 && (
              <Section style={{ flex: 1, marginBottom: 0 }}>
                <DocumentManager patientId={id || ''} />
              </Section>
            )}
          </div>
        </div>
      </div>

      {/* Edit Patient Modal */}
      <EditPatientModal
        open={editPatientModalOpen}
        patient={patient}
        onSave={handleUpdatePatient}
        onCancel={() => setEditPatientModalOpen(false)}
      />
    </div>
  );
};

export default PatientDetail; 